import Button from '@pinecast/common/Button';
import Callout from '@pinecast/common/Callout';
import EmptyState from '@pinecast/common/EmptyState';
import Form from '@pinecast/common/Form';
import Label from '@pinecast/common/Label';
import Section from '@pinecast/common/Section';
import {P} from '@pinecast/common/Text';
import TextArea from '@pinecast/common/TextArea';
import {useManagedMutation} from '@pinecast/gql';
import type {
  LeaveSubscriberFeedback,
  LeaveSubscriberFeedbackVariables,
} from '@pinecast/gql/__generated_tj__/LeaveSubscriberFeedback';
import type {RecurringDonation_tipJarSubscription} from '@pinecast/gql/__generated_tj__/RecurringDonation';
import {FormattedMessage} from '@pinecast/i18n';
import gql from 'graphql-tag';
import * as React from 'react';

const LEAVE_FEEDBACK = gql`
  mutation LeaveSubscriberFeedback($slug: ID!, $message: String!) {
    leaveSubscriberFeedback(podcastSlug: $slug, message: $message)
  }
`;

const RecurringDonationPageFeedback = ({
  subscription,
}: {
  subscription: RecurringDonation_tipJarSubscription;
}) => {
  const leaveFeedback = useManagedMutation<
    LeaveSubscriberFeedback,
    LeaveSubscriberFeedbackVariables
  >(LEAVE_FEEDBACK);
  const [message, setMessage] = React.useState('');
  if (!subscription.podcast.feedbackActive) {
    return (
      <Section>
        <EmptyState
          title={
            <FormattedMessage
              description="Title indicating the the comment box is not active"
              defaultMessage="This show is not accepting comments"
            />
          }
          copy={
            <FormattedMessage
              description="Copy indicating the the comment box is not active"
              defaultMessage="The owner of this podcast has chosen not to accept comments right now."
            />
          }
        />
      </Section>
    );
  }
  return (
    <Section>
      {leaveFeedback.success ? (
        <Callout type="positive">
          <FormattedMessage
            description="Success message when leaving a comment for a podcast"
            defaultMessage="We successfully delivered your comment."
          />
        </Callout>
      ) : leaveFeedback.error ? (
        <Callout type="negative">
          <FormattedMessage
            description="Error message when leaving a comment for a podcast"
            defaultMessage="We weren't able to deliver your comment. Try again later."
          />
        </Callout>
      ) : (
        <P>
          <FormattedMessage
            description="Explanation of submitting a comment to a podcaster"
            defaultMessage="As part of your subscription, you can send messages to the owner of this podcast. Your message, along with your email address, will be shared."
          />
        </P>
      )}
      <Form
        onSubmit={() => {
          leaveFeedback.action({
            variables: {message, slug: subscription.podcast.slug},
          });
        }}
      >
        <Label
          text={
            <FormattedMessage
              description="Label for field to leave a comment for a podcast"
              defaultMessage="Your message"
            />
          }
        >
          <TextArea
            value={message}
            onChange={setMessage}
            disabled={leaveFeedback.pending || leaveFeedback.success}
          />
        </Label>
        <Button
          pending={leaveFeedback.pending}
          disabled={leaveFeedback.success}
          type="submit"
        >
          <FormattedMessage
            description="Button to leave a comment for a podcast"
            defaultMessage="Submit comment"
          />
        </Button>
      </Form>
    </Section>
  );
};

export default RecurringDonationPageFeedback;
